// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@eisinc/App/Components/Common/XngZone";
import { TwoButtonModalStyles } from "@eisinc/Overrides/Components/TwoButtonModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasCartLinesWithErrors,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import saveOrderApproval from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrderApproval";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        accountSettings: { allowGuestCheckout },
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        allowGuestCheckout,
        isApprovalEditDraft: cart?.status === "AwaitingApprovalEditDraft",
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
        isSaveOrderApprovalButtonDisabled:
            isCartLoading ||
            isUpdatingCart ||
            isCartEmpty(cart) ||
            hasCartLinesWithErrors(cart, state.context.showInventory),
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
    saveOrderApproval,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    submitQuoteButtonWrapper?: InjectableCss;
    quoteRequiredModalStyles?: TwoButtonModalStyles;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: fixed;
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            margin-top: 10px;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitRequisitionButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    allowGuestCheckout,
    isApprovalEditDraft,
    orderApprovalDetailsPageUrl,
    saveOrderApproval,
    isSaveOrderApprovalButtonDisabled,
    // XNG-Changes
    id,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);

    // <XNG-Changes>
    let cutsDoesNotEqualQty = false;
    const extendedCutTotal: boolean[] = [];
    let customRequiresQuote = false;
    if (!cart?.cartLines?.length) {
        return null;
    }

    if (cart && cart.cartLines?.length) {
        customRequiresQuote = cart.cartLines.some(line => line.pricing?.unitNetPrice === 0);
        // eslint-disable-next-line array-callback-return
        cart?.cartLines.map(line => {
            if (
                line.properties["allowCutWire"] === "true" &&
                // eslint-disable-next-line no-prototype-builtins
                line.properties?.hasOwnProperty("cutWire") &&
                line.properties["cutWire"] !== ""
            ) {
                const cutObj = JSON.parse(line.properties["cutWire"]);
                const newExtendedTotal = cutObj.reduce(function (prev: any, current: any) {
                    return prev + parseInt(current.total, 10);
                }, 0);
                extendedCutTotal.push(line.qtyOrdered !== newExtendedTotal);
            }
        });
        cutsDoesNotEqualQty = extendedCutTotal.some(o => o);
    }
    // </XNG-Changes>

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        // XNG-Changes: check customRequiresQuote
        if (!quoteRequiredModalIsOpen && (hasQuoteRequiredProducts || customRequiresQuote)) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe && allowGuestCheckout) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    // XNG-Changes : change from Submit for Quote to Request Quote Website
    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Request Quote Website");

    const saveOrderApprovalClickHandler = () => {
        if (!cart) {
            return;
        }

        saveOrderApproval({
            cart,
            onSuccess: () => {
                if (orderApprovalDetailsPageUrl) {
                    history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.orderNumber.split("_")[1]}`);
                }
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <CartTotalDisplay
                showTaxAndShipping={showTaxAndShipping}
                extendedStyles={styles.cartTotal}
                isLoading={isLoading}
                isCartEmpty={isCartEmpty}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
            />
            <XngZone contentId={id} zoneName="Content00" />
            {/* XNG-Changes: change condition to fields.includeActions */}
            {fields.includeActions && (
                <>
                    {/* XNG-Changes: use Submit Requisition button if canSubmitRequisition is true */}
                    {!canSubmitRequisition ? (
                        <Button
                            {...styles.checkoutButton}
                            onClick={checkoutHandler}
                            disabled={
                                isCartCheckoutDisabled ||
                                hasOnlyQuoteRequiredProducts ||
                                // XNG-Changes
                                cart?.cartNotPriced ||
                                cutsDoesNotEqualQty
                            }
                            data-test-selector="cartTotal_checkout"
                        >
                            {cart?.requiresApproval
                                ? translate("Checkout for Approval")
                                : // XNG-Changes : check isPunchOutOrder
                                  isPunchOutOrder
                                  ? translate("Return to Procurement System")
                                  : translate("Checkout")}
                        </Button>
                    ) : (
                        <Button
                            {...styles.submitRequisitionButton}
                            disabled={cart?.cartNotPriced}
                            onClick={submitRequisitionClickHandler}
                            data-test-selector="cartTotal_submitRequisition"
                        >
                            {translate("Submit Requisition")}
                        </Button>
                    )}
                    {isPunchOutOrder && (
                        <Button
                            {...styles.cancelPunchOutButton}
                            // XNG-Changes
                            // disabled={isPunchoutButtonDisabled}
                            disabled={!canCheckoutWithCart}
                            onClick={cancelPunchOutHandler}
                        >
                            {translate("Cancel PunchOut")}
                        </Button>
                    )}
                    {isApprovalEditDraft && (
                        <Button
                            {...styles.checkoutButton}
                            disabled={isSaveOrderApprovalButtonDisabled}
                            onClick={saveOrderApprovalClickHandler}
                            data-test-selector="cartTotal_saveOrderApproval"
                        >
                            {translate("Save")}
                        </Button>
                    )}
                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                        extendedStyles={styles.quoteRequiredModalStyles}
                    ></TwoButtonModal>
                </>
            )}
            {/* XNG-Changes: remove fields.includeActions !== false */}
            {(canSubmitForQuote || customRequiresQuote) && (
                <StyledWrapper {...styles.submitQuoteButtonWrapper}>
                    <Button
                        {...styles.submitQuoteButton}
                        // disabled={isSubmitForQuoteButtonDisabled}
                        onClick={submitForQuoteClickHandler}
                        data-test-selector="cartTotal_submitQuote"
                    >
                        {submitForQuoteLabel}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
